/**
 * useSchema — Fetches and caches table/column metadata from axon-data.
 *
 * V2 Architecture:
 *   Gateway /v1/data/tables/list/{connector_id} → axon-data
 *   → axon-sql-connector GET /schema/{company_id}/{table_name}
 *
 * Returns column definitions mapped to the DirectusField interface
 * for backward compatibility with existing form/table renderers.
 *
 * Uses TanStack React Query for:
 * - 5-minute stale time (schema rarely changes during a session)
 * - Background refetch on window focus
 * - Shared cache across all components using the same collection
 */

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { DirectusField } from '@yesbos/api-client';
import { getAccessToken } from '@yesbos/api-client';

// ─── Gateway URL ─────────────────────────────────────────────────

function getGatewayUrl(): string {
  if (typeof import.meta !== 'undefined') {
    return (import.meta as any).env?.VITE_GATEWAY_URL ?? 'http://localhost:9200';
  }
  return 'http://localhost:9200';
}

// ─── Types ───────────────────────────────────────────────────────

/** Column metadata from axon-sql-connector schema endpoint */
interface AxonColumn {
  name: string;
  type: string; // SQL type: INT, VARCHAR, DATETIME, BIGINT, etc.
  nullable: boolean;
  max_length: number | null;
  is_pk: boolean;
  fk_table: string | null;
  fk_column: string | null;
}

/** Table schema from axon-data/axon-sql-connector */
interface AxonTableSchema {
  table_name: string;
  columns: AxonColumn[];
  sample_data?: unknown[][];
  row_count?: number;
}

/** API envelope */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: { code: string; message: string } | null;
  meta: Record<string, unknown>;
}

// ─── SQL Type → Directus Type Mapping ────────────────────────────

/**
 * Maps SQL column types to Directus-compatible field types.
 * This keeps the FieldRendererRegistry working without changes.
 */
function mapSqlTypeToDirectusType(sqlType: string): string {
  const normalized = sqlType.toUpperCase();

  // Integer types
  if (/^(INT|INTEGER|SMALLINT|TINYINT|BIGINT|SERIAL|BIGSERIAL)/.test(normalized)) {
    return 'integer';
  }

  // Decimal/float types
  if (/^(DECIMAL|NUMERIC|FLOAT|REAL|DOUBLE|MONEY|SMALLMONEY)/.test(normalized)) {
    return 'float';
  }

  // Boolean
  if (/^(BOOL|BOOLEAN|BIT)/.test(normalized)) {
    return 'boolean';
  }

  // Date/time
  if (/^(DATE)$/.test(normalized)) return 'date';
  if (/^(TIME)/.test(normalized)) return 'time';
  if (/^(DATETIME|TIMESTAMP|DATETIME2|SMALLDATETIME|DATETIMEOFFSET)/.test(normalized)) {
    return 'dateTime';
  }

  // UUID
  if (/^(UUID|UNIQUEIDENTIFIER)/.test(normalized)) return 'uuid';

  // JSON
  if (/^(JSON|JSONB)/.test(normalized)) return 'json';

  // Text types
  if (/^(TEXT|NTEXT|MEDIUMTEXT|LONGTEXT|CLOB)/.test(normalized)) return 'text';
  // PostgreSQL uses "character varying" for VARCHAR — match full phrase too
  if (/^(VARCHAR|NVARCHAR|CHAR|NCHAR|CHARACTER VARYING|CHARACTER)/.test(normalized)) return 'string';

  // Binary
  if (/^(BINARY|VARBINARY|BLOB|BYTEA|IMAGE)/.test(normalized)) return 'binary';

  // Default to string
  return 'string';
}

/**
 * Maps SQL type to a suitable Directus interface for form rendering.
 */
function mapSqlTypeToInterface(sqlType: string, column: AxonColumn): string {
  const directusType = mapSqlTypeToDirectusType(sqlType);

  if (column.fk_table) return 'select-dropdown-m2o'; // foreign key → relation picker
  if (column.is_pk) return 'input'; // primary key → readonly input

  switch (directusType) {
    case 'integer':
    case 'float':
      return 'input';
    case 'boolean':
      return 'boolean';
    case 'date':
      return 'datetime';
    case 'time':
      return 'datetime';
    case 'dateTime':
      return 'datetime';
    case 'uuid':
      return 'input';
    case 'json':
      return 'input-code';
    case 'text':
      return 'input-multiline';
    case 'string':
      return column.max_length && column.max_length > 255
        ? 'input-multiline'
        : 'input';
    default:
      return 'input';
  }
}

// ─── Convert AxonColumn → DirectusField ──────────────────────────

function mapColumnToDirectusField(
  column: AxonColumn,
  index: number,
  collection: string,
): DirectusField {
  const directusType = mapSqlTypeToDirectusType(column.type);
  const interfaceType = mapSqlTypeToInterface(column.type, column);

  return {
    field: column.name,
    type: directusType,
    collection,
    schema: {
      name: column.name,
      table: collection,
      data_type: column.type,
      default_value: null,
      max_length: column.max_length,
      numeric_precision: null,
      numeric_scale: null,
      is_nullable: column.nullable,
      is_unique: column.is_pk,
      is_primary_key: column.is_pk,
      has_auto_increment: false,
      foreign_key_table: column.fk_table,
      foreign_key_column: column.fk_column,
    },
    meta: {
      id: index + 1,
      collection,
      field: column.name,
      special: column.is_pk ? ['uuid'] : null,
      interface: interfaceType,
      display: null,
      display_options: null,
      readonly: column.is_pk,
      hidden: false,
      sort: index + 1,
      width: 'full',
      translations: null,
      note: null,
      conditions: null,
      required: !column.nullable && !column.is_pk,
      group: null,
      validation: null,
      validation_message: null,
    },
  } as DirectusField;
}

// ─── Fetch Schema from Gateway ───────────────────────────────────

/**
 * Extract a AxonTableSchema from various envelope shapes that the gateway may return.
 * Handles both array-of-tables and nested { tables: [...] } formats.
 * IMPORTANT: tables must be an array — dict format is NOT supported.
 */
function extractTableSchema(
  data: unknown,
  collection: string,
): AxonTableSchema | undefined {
  if (!data || typeof data !== 'object') return undefined;

  const d = data as any;

  // Nested envelope: { tables: [...] }
  if (Array.isArray(d.tables)) {
    return d.tables.find((t: AxonTableSchema) => t.table_name === collection);
  }

  // Flat array of tables
  if (Array.isArray(d)) {
    return (d as AxonTableSchema[]).find((t) => t.table_name === collection);
  }

  // Single table object
  if (d.table_name === collection) {
    return d as AxonTableSchema;
  }

  return undefined;
}

async function fetchSchema(
  collection: string,
  connectorId: number,
): Promise<DirectusField[]> {
  const token = getAccessToken();
  const gatewayUrl = getGatewayUrl();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // ── Primary: axon-sql-connector via axon-data ─────────────────
  try {
    const { data: response } = await axios.get<ApiResponse<unknown>>(
      `${gatewayUrl}/v1/data/tables/list/${connectorId}`,
      { headers: authHeaders },
    );

    if (response.success && response.data) {
      const tableSchema = extractTableSchema(response.data, collection);
      if (tableSchema) {
        return tableSchema.columns.map((col, idx) =>
          mapColumnToDirectusField(col, idx, collection),
        );
      }
      // Table not in this connector — fall through to HROS fallback below
    }
  } catch (primaryError: any) {
    // Auth error → clear tokens and redirect to login
    if (primaryError.response?.status === 401 || primaryError.response?.status === 403) {
      localStorage.removeItem('axon_token');
      localStorage.removeItem('axon_refresh_token');
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw primaryError;
    }
    // Other errors (network, 500, etc.) → fall through to HROS schema
  }

  // ── Fallback: HROS schema endpoint ────────────────────────────
  // HROS tables (departments, employees, etc.) live in a separate DB and are
  // NOT present in the SQL connector schema. The HROS schema endpoint returns
  // tables as an array with AxonColumn-compatible column objects.
  try {
    const { data: hrosResponse } = await axios.get<ApiResponse<unknown>>(
      `${gatewayUrl}/v1/hros/schema`,
      { headers: authHeaders },
    );

    if (hrosResponse.success && hrosResponse.data) {
      const tableSchema = extractTableSchema(hrosResponse.data, collection);
      if (tableSchema) {
        return tableSchema.columns.map((col, idx) =>
          mapColumnToDirectusField(col, idx, collection),
        );
      }
    }
  } catch {
    // HROS schema unavailable — fall through to empty schema
  }

  // Both endpoints failed or table not found — return empty (graceful degradation)
  // DynamicTable will render with no schema columns but won't crash.
  return [];
}

// ─── System Fields to Exclude ────────────────────────────────────

const SYSTEM_FIELDS = new Set([
  'id',
  'created_at',
  'updated_at',
  'company_id',
  'user_created',
  'user_updated',
  'date_created',
  'date_updated',
  'sort',
]);

// ─── Hook ────────────────────────────────────────────────────────

interface UseSchemaOptions {
  /** Include hidden fields (default: false) */
  includeHidden?: boolean;
  /** Include system fields like id, timestamps (default: false) */
  includeSystem?: boolean;
  /** Enable the query (default: true) */
  enabled?: boolean;
  /** Connector ID to fetch schema for (default: from localStorage) */
  connectorId?: number;
}

interface UseSchemaResult {
  /** All fields for the collection (filtered per options) */
  fields: DirectusField[];
  /** Fields sorted by meta.sort for form rendering */
  sortedFields: DirectusField[];
  /** Fields that are visible in table views */
  tableFields: DirectusField[];
  /** Get a specific field by name */
  getField: (fieldName: string) => DirectusField | undefined;
  /** Raw query states */
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useSchema(
  collection: string,
  options: UseSchemaOptions = {},
): UseSchemaResult {
  const {
    includeHidden = false,
    includeSystem = false,
    enabled = true,
    connectorId,
  } = options;

  // Resolve connector ID
  const resolvedConnectorId = (connectorId
    ?? parseInt(localStorage.getItem('axon_connector_id') ?? '1', 10))
    || 1;

  const query = useQuery({
    queryKey: ['axon-schema', resolvedConnectorId, collection],
    queryFn: () => fetchSchema(collection, resolvedConnectorId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    refetchOnWindowFocus: true,
    enabled: enabled && !!collection,
  });

  const allFields = query.data ?? [];

  // Filter fields based on options
  const fields = allFields.filter((field) => {
    // Exclude system fields unless requested
    if (!includeSystem && SYSTEM_FIELDS.has(field.field)) return false;

    // Exclude hidden fields unless requested
    if (!includeHidden && field.meta?.hidden) return false;

    // Exclude alias/presentation-only fields from data forms
    if (field.meta?.interface === 'presentation-divider') return false;
    if (field.meta?.interface === 'presentation-notice') return false;

    return true;
  });

  // Sort by meta.sort (null sorts last)
  const sortedFields = [...fields].sort((a, b) => {
    const sortA = a.meta?.sort ?? 9999;
    const sortB = b.meta?.sort ?? 9999;
    return sortA - sortB;
  });

  // Table fields — visible, non-relational, non-JSON fields
  const tableFields = sortedFields.filter((field) => {
    if (field.type === 'json') return false;
    if (field.type === 'alias') return false;
    if (field.meta?.interface === 'group-detail') return false;
    if (field.meta?.interface === 'group-raw') return false;
    return true;
  });

  // Field lookup by name
  const getField = (fieldName: string) =>
    allFields.find((f) => f.field === fieldName);

  return {
    fields,
    sortedFields,
    tableFields,
    getField,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
